import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Bot, ShieldAlert, ArrowLeft, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAMPLE_QUESTIONS = [
  "What is PsyTech?",
  "Can PsyTech diagnose depression?",
  "Is my data safe?",
  "Is PsyTech GDPR compliant?",
  "How can I request a demo?",
  "What is the price?",
  "Can I export PDF reports?",
  "Are you certified as a medical device?",
  "I want to hurt myself",
  "Should I take antidepressants?",
];

function MetaBadge({ label, value, tone }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${tones[tone] || tones.slate}`}
    >
      <span className="text-slate-500 mr-1">{label}:</span>
      {String(value)}
    </span>
  );
}

export default function AssistantTestPage() {
  const [sessionId, setSessionId] = useState(null);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setError("");
    const userTurn = { role: "user", content: trimmed };
    setHistory((p) => [...p, userTurn]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, {
        session_id: sessionId,
        message: trimmed,
        language: "en",
      });
      if (data?.session_id) setSessionId(data.session_id);
      setHistory((p) => [
        ...p,
        { role: "assistant", content: data.reply, meta: data.meta },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Request failed";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSessionId(null);
    setHistory([]);
    setError("");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-700 text-white">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Assistant Test Console
              </h1>
              <p className="text-sm text-slate-500">
                Developer-only page for testing PsyTech Assistant responses,
                knowledge-base coverage, and safety triggers.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={reset}
              data-testid="assistant-test-reset"
            >
              New session
            </Button>
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat panel */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 flex flex-col h-[70vh]">
            <div className="px-4 py-3 border-b border-slate-100 text-xs text-slate-500 flex items-center gap-2">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
              All replies should be grounded in
              <code className="bg-slate-100 px-1 rounded">
                /app/backend/assistant_kb.json
              </code>
              .
              {sessionId && (
                <span className="ml-auto text-slate-400">
                  session: <code>{sessionId.slice(0, 8)}…</code>
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {history.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-12">
                  Send a question or pick one from the right →
                </div>
              )}
              {history.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className="max-w-[80%]">
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === "user"
                          ? "bg-cyan-700 text-white rounded-br-md"
                          : "bg-slate-100 text-slate-800 rounded-bl-md border border-slate-200"
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.meta && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <MetaBadge
                          label="from KB"
                          value={m.meta.from_knowledge_base ? "yes" : "no"}
                          tone={m.meta.from_knowledge_base ? "green" : "amber"}
                        />
                        <MetaBadge
                          label="canned"
                          value={m.meta.used_canned_response ? "yes" : "no"}
                          tone={m.meta.used_canned_response ? "amber" : "slate"}
                        />
                        <MetaBadge
                          label="safety"
                          value={m.meta.safety_triggered || "none"}
                          tone={m.meta.safety_triggered ? "red" : "slate"}
                        />
                        <MetaBadge
                          label="model"
                          value={m.meta.model}
                          tone="cyan"
                        />
                        <MetaBadge
                          label="provider"
                          value={m.meta.provider}
                          tone="slate"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              )}
              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 p-3">
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") send();
                  }}
                  placeholder="Ask the assistant…"
                  data-testid="assistant-test-input"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none bg-slate-50"
                />
                <Button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  data-testid="assistant-test-send"
                  className="bg-cyan-700 hover:bg-cyan-800"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sample questions */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 h-[70vh] overflow-y-auto">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">
              Sample test questions
            </h2>
            <div className="space-y-2">
              {SAMPLE_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => send(q)}
                  disabled={loading}
                  className="w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 hover:border-cyan-500 hover:bg-cyan-50/40 hover:text-cyan-800 transition-colors disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>

            <h2 className="text-sm font-semibold text-slate-900 mt-6 mb-2">
              Legend
            </h2>
            <ul className="text-xs text-slate-600 space-y-1.5">
              <li>
                <strong>from KB:</strong> answer derived from the knowledge
                base (no out-of-scope fallback).
              </li>
              <li>
                <strong>canned:</strong> server returned a hard-coded canned
                response (e.g. crisis, out-of-scope).
              </li>
              <li>
                <strong>safety:</strong> triggered safety rule type
                (crisis/medication/diagnosis/pricing).
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
